/**
 * @module services/connectivityService
 * @description Network connectivity detection for EmberPOS.
 * Monitors whether the backend API is reachable by periodically pinging
 * the health endpoint. Also listens to browser online/offline events
 * as a fast-path indicator.
 *
 * Provides an event-emitter-like interface so consumers (React hooks,
 * sync service) can subscribe to connectivity changes.
 */

import { API_BASE_URL } from '../config';

// How often to check connectivity (ms)
const CHECK_INTERVAL_ONLINE = 30000;   // 30s when online (less aggressive)
const CHECK_INTERVAL_OFFLINE = 5000;   // 5s when offline (try to reconnect fast)
const HEALTH_TIMEOUT = 5000;           // 5s timeout for health check

/**
 * @typedef {'online' | 'offline'} ConnectivityStatus
 */

/** @type {ConnectivityStatus} */
let currentStatus = 'online';

/** @type {Set<Function>} */
const listeners = new Set();

/** @type {number|null} */
let checkInterval = null;

/** @type {boolean} */
let initialized = false;

/**
 * Perform a health check against the EmberPOS API.
 * Returns true if the API responds with a 2xx status within the timeout.
 * @returns {Promise<boolean>}
 */
async function checkApiHealth() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT);

    const response = await fetch(`${API_BASE_URL}/api/health`, {
      method: 'GET',
      signal: controller.signal,
      // Skip cache to get a real network check
      cache: 'no-store',
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Update the connectivity status and notify listeners if changed.
 * @param {ConnectivityStatus} newStatus
 */
function setStatus(newStatus) {
  if (newStatus === currentStatus) return;

  const oldStatus = currentStatus;
  currentStatus = newStatus;

  console.log(`[Connectivity] Status changed: ${oldStatus} -> ${newStatus}`);

  // Notify all listeners
  listeners.forEach((fn) => {
    try {
      fn(newStatus, oldStatus);
    } catch (err) {
      console.error('[Connectivity] Listener error:', err);
    }
  });

  // Adjust check interval based on new status
  restartCheckInterval();
}

/**
 * Run a health check and update status accordingly.
 */
async function performCheck() {
  const isReachable = await checkApiHealth();
  setStatus(isReachable ? 'online' : 'offline');
}

/**
 * Restart the periodic check interval with the appropriate frequency.
 */
function restartCheckInterval() {
  if (checkInterval !== null) {
    clearInterval(checkInterval);
  }

  const interval =
    currentStatus === 'online' ? CHECK_INTERVAL_ONLINE : CHECK_INTERVAL_OFFLINE;
  checkInterval = setInterval(performCheck, interval);
}

/**
 * Handle browser online event (fast path).
 */
function handleBrowserOnline() {
  // Browser says we are online -- do an immediate API check to confirm
  performCheck();
}

/**
 * Handle browser offline event (fast path).
 */
function handleBrowserOffline() {
  // Browser says we are offline -- set status immediately (no need to check API)
  setStatus('offline');
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Initialize the connectivity service. Safe to call multiple times.
 * Performs an initial health check and starts periodic monitoring.
 * @returns {Promise<ConnectivityStatus>} Initial status
 */
export async function initConnectivity() {
  if (initialized) return currentStatus;
  initialized = true;

  // Listen to browser connectivity events
  window.addEventListener('online', handleBrowserOnline);
  window.addEventListener('offline', handleBrowserOffline);

  // If browser already reports offline, skip the health check
  if (!navigator.onLine) {
    currentStatus = 'offline';
    restartCheckInterval();
    return currentStatus;
  }

  // Perform initial check
  await performCheck();
  restartCheckInterval();

  return currentStatus;
}

/**
 * Subscribe to connectivity status changes.
 * @param {(status: ConnectivityStatus, previous: ConnectivityStatus) => void} callback
 * @returns {() => void} Unsubscribe function
 */
export function onStatusChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Get the current connectivity status.
 * @returns {ConnectivityStatus}
 */
export function getStatus() {
  return currentStatus;
}

/**
 * Check if the app is currently online.
 * Uses connectivity service status, but falls back to navigator.onLine
 * on native platforms (Capacitor) where the health check may not work reliably.
 * @returns {boolean}
 */
export function isOnline() {
  if (currentStatus === 'online') return true;
  // On Capacitor/native, the health check may fail due to fetch patching.
  // Trust navigator.onLine as fallback so API calls are always attempted.
  if (typeof navigator !== 'undefined' && navigator.onLine && window.Capacitor?.isNativePlatform?.()) {
    return true;
  }
  return false;
}

/**
 * Force an immediate connectivity check.
 * @returns {Promise<ConnectivityStatus>}
 */
export async function forceCheck() {
  await performCheck();
  return currentStatus;
}

/**
 * Stop monitoring (for cleanup/testing).
 */
export function stopMonitoring() {
  if (checkInterval !== null) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
  window.removeEventListener('online', handleBrowserOnline);
  window.removeEventListener('offline', handleBrowserOffline);
  initialized = false;
}

export default {
  initConnectivity,
  onStatusChange,
  getStatus,
  isOnline,
  forceCheck,
  stopMonitoring,
};
